import { useForm } from "@inertiajs/react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Slider } from "@/components/ui/slider"
import { Textarea } from "@/components/ui/textarea"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { DescriptorPicker } from "@/components/DescriptorModal"

interface Sample {
  id: number
  name: string
}

interface Cupping {
  id: number
  title: string
  cupping_form: string
  cupping_form_label: string
  samples: Sample[]
}

interface ExistingCupping {
  sample_id: number
  roast_level: string
  fragrance_intensity: number
  aroma_intensity: number
  flavor_intensity: number
  aftertaste_intensity: number
  acidity_intensity_desc: number
  sweetness_intensity: number
  mouthfeel_intensity: number
  orthonasal_cata_selections: string[]
  flavor_cata_selections: string[]
  main_tastes_cata_selections: string[]
  mouthfeel_cata_selections: string[]
  cva_section_notes: Record<string, string>
  notes: string
}

interface Props {
  cupping: Cupping
  existing_cupping?: ExistingCupping | null
}

const MAIN_TASTES = [
  { key: "salty", label: "Salado" },
  { key: "sour", label: "Acido" },
  { key: "sweet", label: "Dulce" },
  { key: "bitter", label: "Amargo" },
  { key: "umami", label: "Umami" },
]

const MOUTHFEEL_CATA = [
  { key: "rough", label: "Aspero (Arenoso, Yesoso)" },
  { key: "oily", label: "Aceitoso" },
  { key: "smooth", label: "Suave (Aterciopelado, Sedoso, Almibarado)" },
  { key: "mouth_drying", label: "Secante" },
  { key: "metallic", label: "Metalico" },
]

const ROAST_LEVELS = [
  { label: "Claro", value: "light" },
  { label: "Medio-Claro", value: "mid-light" },
  { label: "Medio", value: "medium" },
  { label: "Medio-Oscuro", value: "mid-dark" },
  { label: "Oscuro", value: "dark" },
]

function IntensitySlider({
  label,
  value,
  onChange,
}: {
  label: string
  value: number
  onChange: (v: number) => void
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold">{label}</Label>
        <span className="text-muted-foreground text-sm font-medium">
          {value}
        </span>
      </div>
      <div className="space-y-1">
        <Slider
          value={[value]}
          onValueChange={(v) => onChange(v[0])}
          min={0}
          max={15}
          step={1}
          className="w-full"
        />
        <div className="text-muted-foreground flex justify-between text-[10px]">
          <span>BAJA</span>
          <span>MEDIA</span>
          <span>ALTA</span>
        </div>
      </div>
    </div>
  )
}

interface CataItem {
  key: string
  label: string
  children?: { key: string; label: string }[]
}

function CataSection({
  items,
  selected,
  onToggle,
  maxSelections,
  label,
}: {
  items: CataItem[]
  selected: string[]
  onToggle: (key: string) => void
  maxSelections: number
  label?: string
}) {
  const atLimit = selected.length >= maxSelections
  return (
    <div className="space-y-2">
      {label && (
        <Label className="text-xs font-medium tracking-wide uppercase">
          {label} (max {maxSelections})
        </Label>
      )}
      <div className="flex flex-wrap gap-2">
        {items.map((item) => (
          <div key={item.key} className="flex flex-wrap items-center gap-1">
            <button
              type="button"
              onClick={() => onToggle(item.key)}
              disabled={atLimit && !selected.includes(item.key)}
              className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-all ${
                selected.includes(item.key)
                  ? "bg-primary border-primary text-primary-foreground"
                  : atLimit
                    ? "border-muted text-muted-foreground cursor-not-allowed opacity-50"
                    : "hover:border-primary/50 hover:bg-primary/5"
              }`}
            >
              {item.label}
            </button>
            {item.children?.map((child) => (
              <button
                key={child.key}
                type="button"
                onClick={() => onToggle(child.key)}
                disabled={atLimit && !selected.includes(child.key)}
                className={`rounded-md border px-2 py-0.5 text-[11px] transition-all ${
                  selected.includes(child.key)
                    ? "bg-primary/80 border-primary text-primary-foreground"
                    : atLimit
                      ? "border-muted text-muted-foreground cursor-not-allowed opacity-50"
                      : "hover:border-primary/50 hover:bg-primary/5 border-dashed"
                }`}
              >
                {child.label}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

export default function CvaDescriptiveForm({
  cupping,
  existing_cupping,
}: Props) {
  const [currentSample, setCurrentSample] = useState(0)
  const [showSummaryModal, setShowSummaryModal] = useState(false)
  const [roastLevel, setRoastLevel] = useState(
    existing_cupping?.roast_level || "medium",
  )

  const { data, setData, post, processing } = useForm({
    sample_id: existing_cupping?.sample_id || cupping.samples[0]?.id,
    roast_level: existing_cupping?.roast_level || "medium",
    fragrance_intensity: existing_cupping?.fragrance_intensity || 0,
    aroma_intensity: existing_cupping?.aroma_intensity || 0,
    flavor_intensity: existing_cupping?.flavor_intensity || 0,
    aftertaste_intensity: existing_cupping?.aftertaste_intensity || 0,
    acidity_intensity_desc: existing_cupping?.acidity_intensity_desc || 0,
    sweetness_intensity: existing_cupping?.sweetness_intensity || 0,
    mouthfeel_intensity: existing_cupping?.mouthfeel_intensity || 0,
    orthonasal_cata_selections: (existing_cupping?.orthonasal_cata_selections ||
      []) as string[],
    flavor_cata_selections: (existing_cupping?.flavor_cata_selections ||
      []) as string[],
    main_tastes_cata_selections:
      (existing_cupping?.main_tastes_cata_selections || []) as string[],
    mouthfeel_cata_selections: (existing_cupping?.mouthfeel_cata_selections ||
      []) as string[],
    cva_section_notes: (existing_cupping?.cva_section_notes || {}) as Record<
      string,
      string
    >,
    notes: existing_cupping?.notes || "",
    // Fields required by backend but unused in descriptive
    total_cups: 5,
    fragrance_score: 0,
    fragrance_dry: 0,
    fragrance_break: 0,
    acidity_score: 0,
    acidity_intensity: 0,
    body_score: 0,
    body_level: 0,
    flavor_score: 0,
    aftertaste_score: 0,
    balance_score: 0,
    overall_score: 0,
    uniformity_score: 0,
    clean_cup_score: 0,
    sweetness_score: 0,
    defects_cups: 0,
    defects_intensity: 0,
    re_roast: "no",
  })

  const toggleCata = (
    field:
      | "orthonasal_cata_selections"
      | "flavor_cata_selections"
      | "main_tastes_cata_selections"
      | "mouthfeel_cata_selections",
    key: string,
  ) => {
    const current = data[field]
    if (current.includes(key)) {
      setData(
        field,
        current.filter((k) => k !== key),
      )
    } else {
      setData(field, [...current, key])
    }
  }

  const getSectionNote = (key: string) => data.cva_section_notes[key] || ""

  const setSectionNote = (key: string, value: string) => {
    setData("cva_section_notes", {
      ...data.cva_section_notes,
      [key]: value,
    })
  }

  const handleSampleChange = (index: number) => {
    setCurrentSample(index)
    setData("sample_id", cupping.samples[index]?.id)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setShowSummaryModal(true)
  }

  const handleConfirmSubmit = () => {
    if (!cupping.samples?.length) {
      alert("Error: Esta sesion no tiene muestras asociadas.")
      return
    }
    const sampleId = data.sample_id || cupping.samples[currentSample]?.id
    if (!sampleId) {
      alert("Error: No se pudo identificar la muestra.")
      return
    }
    if (data.sample_id !== sampleId) {
      setData("sample_id", sampleId)
    }
    post(`/cuppings/${cupping.id}/cup`)
  }

  const sample = cupping.samples[currentSample]

  return (
    <>
      <Card>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Sample Selector */}
            <div className="flex items-center gap-4 border-b pb-4">
              <div className="flex items-center gap-2">
                {cupping.samples.map((s: Sample, idx: number) => (
                  <Button
                    key={s.id}
                    type="button"
                    variant={currentSample === idx ? "default" : "outline"}
                    onClick={() => handleSampleChange(idx)}
                    className={currentSample === idx ? "bg-primary" : ""}
                  >
                    {idx + 1}
                  </Button>
                ))}
              </div>
              <div className="flex-1" />
              <div className="text-right">
                <p className="text-muted-foreground text-sm">
                  {sample?.name || `Muestra #${currentSample + 1}`}
                </p>
              </div>
            </div>

            {/* Roast Level */}
            <div className="flex items-center gap-3">
              <Label className="text-sm font-semibold">Nivel de tueste</Label>
              <div className="flex items-center gap-2">
                {ROAST_LEVELS.map((level) => (
                  <button
                    key={level.value}
                    type="button"
                    onClick={() => {
                      setRoastLevel(level.value)
                      setData("roast_level", level.value)
                    }}
                    className={`flex flex-col items-center gap-1 rounded-lg p-1.5 transition-colors ${
                      roastLevel === level.value
                        ? "bg-primary/10"
                        : "hover:bg-accent"
                    }`}
                  >
                    <div
                      className={`h-8 w-8 rounded-full border-2 ${
                        roastLevel === level.value
                          ? "border-primary"
                          : "border-muted-foreground/30"
                      }`}
                      style={{
                        backgroundColor:
                          level.value === "light"
                            ? "#D4A574"
                            : level.value === "mid-light"
                              ? "#B8865F"
                              : level.value === "medium"
                                ? "#8B5A3C"
                                : level.value === "mid-dark"
                                  ? "#6B4423"
                                  : "#3E2723",
                      }}
                    />
                    <span className="text-[10px]">{level.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* === FRAGRANCE & AROMA === */}
            <Card>
              <CardContent className="space-y-4 p-4">
                <h3 className="border-b pb-2 text-sm font-bold tracking-wide uppercase">
                  Fragancia y Aroma
                </h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <IntensitySlider
                    label="Fragancia - Intensidad"
                    value={data.fragrance_intensity}
                    onChange={(v) => setData("fragrance_intensity", v)}
                  />
                  <IntensitySlider
                    label="Aroma - Intensidad"
                    value={data.aroma_intensity as number}
                    onChange={(v) => setData("aroma_intensity", v)}
                  />
                </div>
                <DescriptorPicker
                  selected={data.orthonasal_cata_selections}
                  onApply={(sel) => setData("orthonasal_cata_selections", sel)}
                  maxSelections={5}
                  title="Descriptores ortonasales (max 5)"
                />
                <Textarea
                  value={getSectionNote("fragrance_aroma")}
                  onChange={(e) =>
                    setSectionNote("fragrance_aroma", e.target.value)
                  }
                  placeholder="Notas de fragancia y aroma..."
                  rows={2}
                  className="resize-none text-sm"
                />
              </CardContent>
            </Card>

            {/* === FLAVOR & AFTERTASTE === */}
            <Card>
              <CardContent className="space-y-4 p-4">
                <h3 className="border-b pb-2 text-sm font-bold tracking-wide uppercase">
                  Sabor y Retrogusto
                </h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <IntensitySlider
                    label="Sabor - Intensidad"
                    value={data.flavor_intensity}
                    onChange={(v) => setData("flavor_intensity", v)}
                  />
                  <IntensitySlider
                    label="Retrogusto - Intensidad"
                    value={data.aftertaste_intensity}
                    onChange={(v) => setData("aftertaste_intensity", v)}
                  />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <DescriptorPicker
                    selected={data.flavor_cata_selections}
                    onApply={(sel) => setData("flavor_cata_selections", sel)}
                    maxSelections={5}
                    title="Descriptores retronasales (max 5)"
                  />
                  <CataSection
                    items={MAIN_TASTES}
                    selected={data.main_tastes_cata_selections}
                    onToggle={(key) =>
                      toggleCata("main_tastes_cata_selections", key)
                    }
                    maxSelections={2}
                    label="Gustos principales"
                  />
                </div>
                <Textarea
                  value={getSectionNote("flavor_aftertaste")}
                  onChange={(e) =>
                    setSectionNote("flavor_aftertaste", e.target.value)
                  }
                  placeholder="Notas de sabor y retrogusto..."
                  rows={2}
                  className="resize-none text-sm"
                />
              </CardContent>
            </Card>

            {/* === ACIDITY & SWEETNESS === */}
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardContent className="space-y-4 p-4">
                  <h3 className="border-b pb-2 text-sm font-bold tracking-wide uppercase">
                    Acidez
                  </h3>
                  <IntensitySlider
                    label="Intensidad"
                    value={data.acidity_intensity_desc}
                    onChange={(v) => setData("acidity_intensity_desc", v)}
                  />
                  <div className="space-y-2">
                    <Label className="text-xs font-medium tracking-wide uppercase">
                      Tipo de acidez (selecciona 1)
                    </Label>
                    <div className="flex flex-col gap-2">
                      {[
                        {
                          value: "dry",
                          label: "Acidez seca",
                          desc: "Herbácea, Vegetal",
                        },
                        {
                          value: "sweet",
                          label: "Acidez dulce",
                          desc: "Jugosa, Afrutada",
                        },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() =>
                            setSectionNote("acidity_type", opt.value)
                          }
                          className={`rounded-md border px-3 py-2 text-left text-sm transition-all ${
                            getSectionNote("acidity_type") === opt.value
                              ? "bg-primary/10 border-primary text-primary font-medium"
                              : "hover:border-primary/50 hover:bg-primary/5"
                          }`}
                        >
                          {opt.label}{" "}
                          <span className="text-muted-foreground text-xs">
                            ({opt.desc})
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <Textarea
                    value={getSectionNote("acidity")}
                    onChange={(e) => setSectionNote("acidity", e.target.value)}
                    placeholder="Descriptores de acidez..."
                    rows={2}
                    className="resize-none text-sm"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardContent className="space-y-4 p-4">
                  <h3 className="border-b pb-2 text-sm font-bold tracking-wide uppercase">
                    Dulzura
                  </h3>
                  <IntensitySlider
                    label="Intensidad"
                    value={data.sweetness_intensity}
                    onChange={(v) => setData("sweetness_intensity", v)}
                  />
                  <Textarea
                    value={getSectionNote("sweetness")}
                    onChange={(e) =>
                      setSectionNote("sweetness", e.target.value)
                    }
                    placeholder="Descriptores de dulzura..."
                    rows={2}
                    className="resize-none text-sm"
                  />
                </CardContent>
              </Card>
            </div>

            {/* === MOUTHFEEL === */}
            <Card>
              <CardContent className="space-y-4 p-4">
                <h3 className="border-b pb-2 text-sm font-bold tracking-wide uppercase">
                  Sensacion en boca
                </h3>
                <IntensitySlider
                  label="Intensidad"
                  value={data.mouthfeel_intensity}
                  onChange={(v) => setData("mouthfeel_intensity", v)}
                />
                <CataSection
                  items={MOUTHFEEL_CATA}
                  selected={data.mouthfeel_cata_selections}
                  onToggle={(key) =>
                    toggleCata("mouthfeel_cata_selections", key)
                  }
                  maxSelections={2}
                  label="Textura"
                />
                <Textarea
                  value={getSectionNote("mouthfeel")}
                  onChange={(e) => setSectionNote("mouthfeel", e.target.value)}
                  placeholder="Notas de sensacion en boca..."
                  rows={2}
                  className="resize-none text-sm"
                />
              </CardContent>
            </Card>

            {/* === GENERAL NOTES === */}
            <Card>
              <CardContent className="space-y-3 p-4">
                <h3 className="font-semibold">Notas generales</h3>
                <Textarea
                  value={data.notes}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setData("notes", e.target.value)
                  }
                  placeholder="Notas adicionales..."
                  rows={4}
                  className="resize-none"
                />
              </CardContent>
            </Card>

            {/* Submit */}
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={processing}
                size="lg"
                className="bg-primary hover:bg-primary/90"
              >
                Revisar y Enviar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Summary Modal */}
      <Dialog open={showSummaryModal} onOpenChange={setShowSummaryModal}>
        <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              Resumen - Evaluacion Descriptiva CVA
            </DialogTitle>
            <DialogDescription>
              Revisa tu perfil descriptivo antes de enviar
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-primary/10 rounded-lg p-4">
              <h3 className="mb-2 font-semibold">Informacion de Muestra</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Muestra:</span>{" "}
                  <span className="font-medium">
                    {sample?.name || `Muestra #${currentSample + 1}`}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Tueste:</span>{" "}
                  <span className="font-medium capitalize">{roastLevel}</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Intensidades (0-15)</h4>
              <div className="grid grid-cols-2 gap-1 text-sm">
                {[
                  { label: "Fragancia", value: data.fragrance_intensity },
                  { label: "Aroma", value: data.aroma_intensity },
                  { label: "Sabor", value: data.flavor_intensity },
                  { label: "Retrogusto", value: data.aftertaste_intensity },
                  { label: "Acidez", value: data.acidity_intensity_desc },
                  { label: "Dulzura", value: data.sweetness_intensity },
                  {
                    label: "Sensacion en boca",
                    value: data.mouthfeel_intensity,
                  },
                ].map((item) => (
                  <div key={item.label} className="flex justify-between">
                    <span>{item.label}</span>
                    <span className="font-semibold">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {data.orthonasal_cata_selections.length > 0 && (
              <div className="space-y-1">
                <h4 className="text-sm font-semibold">
                  Descriptores ortonasales
                </h4>
                <p className="text-muted-foreground text-sm">
                  {data.orthonasal_cata_selections.join(", ")}
                </p>
              </div>
            )}
            {data.flavor_cata_selections.length > 0 && (
              <div className="space-y-1">
                <h4 className="text-sm font-semibold">
                  Descriptores retronasales
                </h4>
                <p className="text-muted-foreground text-sm">
                  {data.flavor_cata_selections.join(", ")}
                </p>
              </div>
            )}
            {data.main_tastes_cata_selections.length > 0 && (
              <div className="space-y-1">
                <h4 className="text-sm font-semibold">Gustos principales</h4>
                <p className="text-muted-foreground text-sm">
                  {data.main_tastes_cata_selections.join(", ")}
                </p>
              </div>
            )}
            {data.mouthfeel_cata_selections.length > 0 && (
              <div className="space-y-1">
                <h4 className="text-sm font-semibold">Sensacion en boca</h4>
                <p className="text-muted-foreground text-sm">
                  {data.mouthfeel_cata_selections.join(", ")}
                </p>
              </div>
            )}

            <div className="bg-muted rounded-lg p-4 text-sm">
              <p className="text-muted-foreground">
                La evaluacion descriptiva no genera una puntuacion. Su proposito
                es perfilar y caracterizar los atributos sensoriales del cafe de
                forma objetiva.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowSummaryModal(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleConfirmSubmit}
              disabled={processing}
              className="bg-primary hover:bg-primary/90"
            >
              {processing ? "Enviando..." : "Confirmar y Enviar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
